import {
  Directive,
  EventEmitter,
  OnInit,
  Output,
  Input,
  signal,
  HostListener,
  Inject,
  PLATFORM_ID,
  OnChanges,
  SimpleChanges,
  Component
} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {ActivatedRoute, Router} from '@angular/router';
import {HttpErrorResponse, HttpStatusCode} from "@angular/common/http";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {CustomDatePipe, ResponseHttpSuccess, CustomMessageService, UtilService, LocalStorageService} from "library";
import {CategoryRequestService} from "projects/store/src/app/services/api/v1/category-request.service";
import {UserValidatorService} from "projects/store/src/app/services/api/v1/user-validator.service";
import {environment} from "projects/store/src/environments/environment";
import {RequestService} from "projects/store/src/app/services/api/v1/request.service";
import {LocalStorageKey, SalesWorkflow} from "projects/store/src/app/enums";
import {PixelService} from "projects/store/src/app/services/api/v1/pixel.service";

export enum Steps {
  'simulation' = 'simulation',
  'results' = 'results',
  'form' = 'form',
}

/**
 * Componente principal de Simulador Kredicash Embebido
 * Contiene toda la lógica común para V2 y V3
 * Actúa como container que renderiza la versión correcta según el flujo
 */
@Component({
  selector: 'app-sales-simulator-kredicash-embedded',
  templateUrl: './sales-simulator-kredicash-embedded.component.html',
  providers: [CustomDatePipe]
})
export class SalesSimulatorKredicashEmbeddedComponent implements OnInit, OnChanges {
  products: any = signal<any>([]);
  minPrice = signal<number>(1000000000);
  maxPrice = signal<number>(0);
  minDay = signal<number>(15);
  maxDay = signal<number>(0);
  prices: any = signal<any>([]);
  isSubmitted = signal<boolean>(false);
  isLoading = signal<boolean>(false);
  simulationsResults = signal<any>({});
  dayOptions: any[] = [];
  selectedProduct: any;
  selectedPrice!: number;
  selectedDay!: number;
  country = environment.global.country;
  form!: FormGroup;
  flowCode: string;

  step = Steps.simulation;
  @Output() nextStepEvent = new EventEmitter();

  // Modal mode inputs
  @Input() mode: 'normal' | 'modal' = 'normal';
  @Input() initialProducts: any[] = [];
  @Input() initialSalary: number | null = null;
  @Input() externalCategoryId: string = '';
  @Input() requestId: string = '';
  @Output() modalSaveEvent = new EventEmitter<any>();

  // Iframe-specific properties
  isEmbedded = false;
  parentOrigin = '*';
  autoAdvanceOnSimulation = false;

  constructor(
    protected categoryRequestService: CategoryRequestService,
    protected utilService: UtilService,
    protected customMessageService: CustomMessageService,
    protected customDatePipe: CustomDatePipe,
    protected userValidatorService: UserValidatorService,
    protected requestService: RequestService,
    protected localStorageService: LocalStorageService,
    protected metaPixelService: PixelService,
    protected route: ActivatedRoute,
    protected router: Router,
    @Inject(PLATFORM_ID) protected platformId: Object
  ) {
    this.flowCode = this.route.snapshot.params['flow_code'];
    this.form = new FormGroup({
      amount: new FormControl(''),
      quota_quantity: new FormControl(''),
      salary: new FormControl(null, this.mode === 'modal' ? Validators.required : null),
      frecuency: new FormControl('')
    });
  }

  /**
   * Determina si el flujo actual es Krdya (V3)
   */
  get isFlowKrdya(): boolean {
    return this.flowCode === SalesWorkflow['app-krdya-kredicash'];
  }

  ngOnInit() {
    // Solo establecer el paso a 1 si NO está en modo modal
    if (this.mode !== 'modal') {
      this.requestService.step.set(1);
    }

    // Detect if we're in iframe mode
    this.detectIframeMode();

    // Initialize form with proper salary
    this.form.patchValue({
      salary: this.initialSalary
    });

    this.load();

    // Listen to salary changes for auto-simulation
    this.form.get('salary')?.valueChanges.subscribe(() => {
      if (this.form.get('salary')?.value) {
        this.autoSimulate();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['initialProducts'] && changes['initialProducts'].currentValue) {
      this.products.set(changes['initialProducts'].currentValue);
      this.onExtractMinMaxPrices();
    }

    if (changes['initialSalary'] && changes['initialSalary'].currentValue) {
      this.form.patchValue({
        salary: changes['initialSalary'].currentValue
      });
    }

    if (changes['externalCategoryId'] && changes['externalCategoryId'].currentValue) {
      this.requestService.categoryId = changes['externalCategoryId'].currentValue;
    }
  }

  detectIframeMode() {
    if (isPlatformBrowser(this.platformId)) {
      // Check if we're in iframe mode by looking at the URL or query params
      this.isEmbedded = window.self !== window.top ||
        this.router.url.includes('simulator-kredicash-iframe') ||
        this.router.url.includes('iframe/ventas/');
      this.autoAdvanceOnSimulation = this.isEmbedded;

      // Get query parameters for iframe configuration
      this.route.queryParams.subscribe(params => {
        if (params['categoryId']) {
          this.requestService.categoryId = params['categoryId'];
        }
        if (params['autoAdvance'] === 'true') {
          this.autoAdvanceOnSimulation = true;
        }
      });

      // Ensure we have a category ID set
      if (!this.requestService.categoryId) {
        this.requestService.categoryId = '0ee82679-0154-11ee-ba26-42010a8a0002';
      }
    }
  }

  load() {
    // Skip loading if we're in modal mode and already have products
    if (this.mode === 'modal' && this.initialProducts.length > 0) {
      this.products.set(this.initialProducts);
      this.onExtractMinMaxPrices();
      return;
    }

    this.utilService.enablePageLoading();
    this.localStorageService.remove(LocalStorageKey.simulation);
    this.categoryRequestService.getProductsByCategory(this.requestService.categoryId).subscribe({
      next: (resp: ResponseHttpSuccess) => {
        if (resp.status === HttpStatusCode.Ok) {
          this.products.set(resp.body.data.list)
          this.onExtractMinMaxPrices();
        } else {
          this.customMessageService.responseError();
          this.utilService.disablePageLoading();
        }
      },
      error: (err: HttpErrorResponse) => {
        this.customMessageService.comunicationError();
        this.utilService.disablePageLoading();
      }
    })
  }

  onExtractMinMaxPrices() {
    let availableDays: number[] = [];
    this.products().forEach((row: any) => {
      row.quotes.forEach((row1: any) => {
        this.minPrice.set(row1.prices.suggested < this.minPrice() ? row1.prices.suggested : this.minPrice());
        this.maxPrice.set(row1.prices.suggested > this.maxPrice() ? row1.prices.suggested : this.maxPrice());
        const currentDay = row1.quota.quantity * 15;
        availableDays.push(currentDay)

        if (!this.prices().includes(row1.prices.suggested)) {
          this.prices.update((prices: any) => [...prices, row1.prices.suggested])
        }

        if (row1.prices.suggested == this.maxPrice()) {
          this.selectedProduct = row;
        }
      });
    });
    const uniqueDays = availableDays
      .filter((item, index) => availableDays.indexOf(item) === index)
      .sort((a, b) => Number(a) - Number(b));
    this.minDay.set(uniqueDays[0])
    this.maxDay.set(uniqueDays[uniqueDays.length - 1])
    this.selectedPrice = this.maxPrice();
    this.selectedDay = this.maxDay();
    this.onExtractDaysForSelected();

    // Auto-simulate once data is loaded
    this.autoSimulate();

    this.utilService.disablePageLoading();
  }

  onExtractDaysForSelected() {
    const min = this.minDay();
    const max = this.maxDay();

    this.dayOptions = [];

    for (let i = min; i <= max; i += 60) {
      this.dayOptions.push(i);
    }
  }

  simulate() {
    this.isSubmitted.set(true);
    if (this.form.invalid) {
      return;
    }

    this.form.patchValue({
      amount: this.selectedPrice,
      quota_quantity: this.selectedDay / 15,
      frecuency: 15
    });

    const simulationObservable = this.mode === 'modal' && this.requestId
      ? this.categoryRequestService.simulateCreditForModal(this.requestId, this.form.value)
      : this.categoryRequestService.simulateCredit(this.requestService.categoryId, this.form.value);

    simulationObservable.subscribe({
      next: (resp: ResponseHttpSuccess) => {
        if (resp.status === HttpStatusCode.Ok) {
          this.simulationsResults.set(resp.body.data);
          this.step = Steps.results;
          this.isSubmitted.set(false);
          console.log(this.simulationsResults());
        } else {
          this.customMessageService.responseError();
        }
      },
      error: (err: HttpErrorResponse) => {
        this.customMessageService.responseError();
      }
    });
  }

  onSlideEnd(price: any) {
    this.onParameterChange(price);
  }

  onParameterChange(price?: any) {
    if (price !== undefined) {
      if (!this.prices().includes(price)) {
        let previousPrice = this.selectedProduct.quotes[0].prices.suggested
        let priceTemp: any;
        if (price < previousPrice) {
          this.prices().sort((a: any, b: any) => b - a).forEach((row: any) => {
            if (row < price && priceTemp == undefined) {
              priceTemp = row;
            }
          });
        } else {
          this.prices().sort((a: any, b: any) => a - b).forEach((row: any) => {
            if (row > price && priceTemp == undefined) {
              priceTemp = row;
            }
          });
        }

        if (priceTemp) {
          this.selectedPrice = priceTemp;
        }
      }
      this.selectedProduct = this.getProductByPrice(this.selectedPrice);
    }
    this.autoSimulate();
  }

  onDayChange(day: number) {
    this.selectedDay = day;
    this.autoSimulate();
  }

  autoSimulate() {
    this.form.patchValue({
      amount: this.selectedPrice,
      quota_quantity: this.selectedDay / 15,
      frecuency: 15
    });

    // Use different endpoint based on mode
    const simulationObservable = this.mode === 'modal' && this.requestId
      ? this.categoryRequestService.simulateCreditForModal(this.requestId, this.form.value)
      : this.categoryRequestService.simulateCredit(this.requestService.categoryId, this.form.value);

    simulationObservable.subscribe({
      next: (resp: ResponseHttpSuccess) => {
        if (resp.status === HttpStatusCode.Ok) {
          this.simulationsResults.set(resp.body.data);

          // Auto-advance to next step if in iframe mode
          if (this.autoAdvanceOnSimulation && this.simulationsResults().quota_from) {
            setTimeout(() => {
              this.onSave();
            }, 1000); // Wait 1 second to show the simulation results
          }
        }
      },
      error: () => {
        // Silently handle errors during auto-simulation
      }
    });
  }

  getProductByPrice(price: number) {
    let product = null;
    this.products().forEach((row: any) => {
      row.quotes.forEach((row1: any) => {
        if (row1.prices.suggested === price) {
          row.quotes.sort((a: any, b: any) => a.quota.quantity - b.quota.quantity);
          product = row;
        }
      });
    });
    return product;
  }

  onContinue() {
    console.log('entro')
    console.log(this.simulationsResults())

    // Handle modal mode
    if (this.mode === 'modal') {
      this.onModalSave();
      return;
    }

    // If we already have simulation results, continue to next step
    if (this.simulationsResults() && this.simulationsResults().quota_from) {
      this.onSave();
    } else {
      this.simulate();
    }
  }

  onModalSave() {
    this.isSubmitted.set(true);

    if (this.form.invalid) {
      return;
    }

    // Prepare data in the same format as updateCredit() in sales-credit-summary-kredicash
    const selectedQuota = this.selectedDay / 15; // Assuming frecuency is always 15
    const data = {
      selectedProduct: this.selectedProduct,
      selectedPrice: this.selectedPrice,
      selectedDay: this.selectedDay,
      selectedQuota: selectedQuota,
      formData: this.form.value,
      product: {
        id: this.selectedProduct.id,
        code: this.selectedProduct.code,
        name: this.selectedProduct.name
      },
      quotes: this.selectedProduct.quotes.filter((row: any) =>
        row.prices.suggested === this.selectedPrice &&
        row.quota.quantity === selectedQuota
      )[0]
    };

    // Add additional properties to quotes
    if (data.quotes) {
      data.quotes.frecuency = 15;
      data.quotes.quota.selected = data.quotes.quota.from;
      data.quotes.prices.selected = data.quotes.prices.suggested;
    }

    this.modalSaveEvent.emit(data);
  }

  onContinueIframe() {
    // Handle iframe-specific logic
    if (this.simulationsResults() && this.simulationsResults().quota_from) {
      this.onSaveForIframe();
    } else {
      this.simulate();
    }
  }

  onSaveForIframe() {
    this.metaPixelService.setLoanData(this.selectedPrice, 15, this.selectedDay / 15);

    const simulationData = {...this.form.value, ...this.simulationsResults()};
    this.localStorageService.set(LocalStorageKey.simulation, simulationData);

    // Si está dentro de un iframe, salir del iframe y continuar en la página principal
    if (window.self !== window.top) {
      const nextStepUrl = this.buildNextStepUrl();

      // Encode simulation data as base64 to pass via query params
      const encodedSimulation = encodeURIComponent(btoa(JSON.stringify(simulationData)));

      // Capture query params
      const queryParams = this.route.snapshot.queryParams;

      // Separate start_token from UTM params
      const startToken = queryParams['start_token'];
      const utmData: any = {};
      Object.keys(queryParams).forEach(key => {
        if (key !== 'start_token') {
          utmData[key] = queryParams[key];
        }
      });

      // Build query string with URL-encoded base64
      const params: any = {simulation_data: encodedSimulation};

      // Add start_token as separate parameter
      if (startToken) {
        params.start_token = startToken;
      }

      // Add UTM data as separate parameter
      if (Object.keys(utmData).length > 0) {
        params.utm_data = encodeURIComponent(btoa(JSON.stringify(utmData)));
      }

      const queryString = new URLSearchParams(params).toString();

      // Use absolute URL with current origin (works in local and production)
      const absoluteUrl = `${window.location.origin}/${nextStepUrl}?${queryString}`;
      console.log('🚀 Navegando desde iframe a:', absoluteUrl);

      window.top!.location.href = absoluteUrl;
    } else {
      // Si no está en iframe, continuar normal
      this.navigateToIdentificationStep();
    }
  }

  protected navigateToIdentificationStep() {
    const nextStepUrl = this.buildNextStepUrl();

    // Navigate directly in iframe
    this.router.navigate([nextStepUrl]);
  }

  onSave() {
    this.metaPixelService.setLoanData(this.selectedPrice, 15, this.selectedDay / 15);

    // Only track event if not in iframe mode to avoid workflow undefined error
    if (!this.isEmbedded) {
      this.metaPixelService.trackEvent('InitiateCheckout');
    }

    // Capture and save UTM parameters when in iframe mode
    if (this.isEmbedded) {
      this.captureAndSaveUtmParams();
    }

    const simulationData = {...this.form.value, ...this.simulationsResults()};
    this.localStorageService.set(LocalStorageKey.simulation, simulationData);

    // En el flujo app-krdya-kredicash, persistir la simulación al backend
    const reqId = this.requestId || this.route.snapshot.params['request_id'];
    if (this.isFlowKrdya && reqId) {
      this.requestService.update(reqId, { simulation: simulationData }).subscribe({
        next: () => this.onNextStep(),
        error: () => this.onNextStep()
      });
    } else {
      this.onNextStep();
    }
  }

  onPreviousStep(step: string) {
    this.step = Steps[step as Steps];
  }

  onNextStep() {
    // Also emit the local event (for non-iframe usage)
    this.nextStepEvent.emit();
  }

  protected buildNextStepUrl(): string {
    // Build URL for kredicash client identification step
    // Format: ventas/kredicash-cliente/categoria/kredicash/identificacion-cliente
    const flowCode = 'kredicash-cliente';
    const categoryCode = 'kredicash';
    const path = 'identificacion-cliente';

    return `ventas/${flowCode}/categoria/${categoryCode}/${path}`;
  }

  @HostListener('window:message', ['$event'])
  onMessage(event: MessageEvent) {
    // Handle messages from parent
    if (this.isEmbedded && isPlatformBrowser(this.platformId)) {
      switch (event.data.type) {
        case 'setParams':
          if (event.data.params) {
            // Allow parent to set simulation parameters
            if (event.data.params.categoryId) {
              this.requestService.categoryId = event.data.params.categoryId;
            }
            if (event.data.params.salary) {
              this.form.patchValue({salary: event.data.params.salary});
            }
          }
          break;
        case 'resize':
          // Handle resize requests from parent
          break;
      }
    }
  }

  postMessageToParent(type: string, data: any) {
    if (this.isEmbedded && isPlatformBrowser(this.platformId)) {
      window.parent.postMessage({
        type: `kredicash-simulator-${type}`,
        data: data,
        timestamp: Date.now()
      }, this.parentOrigin);
    }
  }

  get f() {
    return this.form.controls;
  }

  protected captureAndSaveUtmParams(): void {
    const queryParams = this.route.snapshot.queryParams;

    const utmData: any = {};
    let hasParams = false;

    // Capturar todos los parámetros excepto start_token
    Object.keys(queryParams).forEach(key => {
      if (key !== 'start_token') {
        utmData[key] = queryParams[key];
        hasParams = true;
      }
    });

    if (hasParams) {
      this.localStorageService.set(LocalStorageKey.utmData, utmData);
    }
  }

  // Métodos para incrementar/decrementar el monto
  incrementPrice() {
    const pricesList = this.prices().sort((a: any, b: any) => a - b);
    const currentIndex = pricesList.indexOf(this.selectedPrice);
    if (currentIndex < pricesList.length - 1) {
      this.selectedPrice = pricesList[currentIndex + 1];
      this.selectedProduct = this.getProductByPrice(this.selectedPrice);
      this.autoSimulate();
    }
  }

  decrementPrice() {
    const pricesList = this.prices().sort((a: any, b: any) => a - b);
    const currentIndex = pricesList.indexOf(this.selectedPrice);
    if (currentIndex > 0) {
      this.selectedPrice = pricesList[currentIndex - 1];
      this.selectedProduct = this.getProductByPrice(this.selectedPrice);
      this.autoSimulate();
    }
  }

  canIncrementPrice(): boolean {
    const pricesList = this.prices().sort((a: any, b: any) => a - b);
    const currentIndex = pricesList.indexOf(this.selectedPrice);
    return currentIndex < pricesList.length - 1;
  }

  canDecrementPrice(): boolean {
    const pricesList = this.prices().sort((a: any, b: any) => a - b);
    const currentIndex = pricesList.indexOf(this.selectedPrice);
    return currentIndex > 0;
  }

  protected readonly Steps = Steps;
}
